import React from 'react'
import PropTypes from 'prop-types'
import Select from 'react-select'
import classnames from 'classnames'

import './Chosen.scss'

export const Chosen = ({
  className,
  extraLinkCallback,
  extraLinkLabel,
  filterOptionMethod,
  handleUndefinedValue,
  invalidMessage,
  isClearable,
  isDisabled,
  isInvalid,
  isLoading,
  isMulti,
  isSearchable,
  label,
  labelRequired,
  limitSearch,
  limitSearchText,
  name,
  noResultsText,
  notes,
  onBlur,
  onChange,
  onInputChange,
  onInputReset,
  options,
  placeholder,
  value,
  'data-mu-test': dataAttributeTest
}) => {
  const flattenedOptions = options.reduce((accumulator, currentValue) => {
    return Object.prototype.hasOwnProperty.call(currentValue, 'options') &&
      Array.isArray(currentValue.options)
      ? [...accumulator, ...currentValue.options]
      : [...accumulator, currentValue]
  }, [])

  const srcValue = Array.isArray(value) ? value : [value]

  const selectedValue = srcValue.map(singleValue => {
    return flattenedOptions.find(singleOption => {
      return singleOption.value === singleValue
    })
  })

  const chosenOptions = [
    ...(handleUndefinedValue
      ? [
        {
          label: placeholder,
          value: undefined
        }
      ]
      : []),
    ...options
  ]

  if (limitSearch) {
    chosenOptions.push({ label: limitSearchText, disabled: true })
  }

  const filterOption =
    filterOptionMethod === 'contains' ? null : filterOptionStartsWith

  const classContainer = classnames(className, {
    'react_select__container-invalid': isInvalid,
    'react_select__container--handle-undefined': handleUndefinedValue
  })

  return (
    <div
      className={classContainer}
      data-mu-test={classnames('select', dataAttributeTest)}
    >
      {label && (
        <div className='row-label'>
          <label>{label}</label>
          {labelRequired && <strong> *</strong>}
          {extraLinkLabel && (
            <a
              className='textlink-extra-light f-right'
              onClick={extraLinkCallback}
            >
              {extraLinkLabel}
            </a>
          )}
        </div>
      )}
      <div className='row m-bottom-0'>
        <Select
          className='react_select__container'
          classNamePrefix='react_select'
          filterOption={filterOption}
          imitSearch={limitSearch}
          isClearable={isClearable}
          isDisabled={isDisabled}
          isInvalid={isInvalid}
          isLoading={isLoading}
          isMulti={isMulti}
          isOptionDisabled={option => option.disabled}
          isSearchable={isSearchable}
          limitSearchText={limitSearchText}
          name={name}
          noOptionsMessage={() => noResultsText}
          onBlur={onBlur}
          onChange={onChange}
          onInputChange={onInputChange}
          onMenuOpen={onInputReset}
          options={chosenOptions}
          placeholder={placeholder}
          value={selectedValue}
        />
      </div>
      {isInvalid && isInvalid && (
        <div className='row m-bottom-0'>
          <p className='input_is-required_text'>{invalidMessage}</p>
        </div>
      )}
      {notes && (
        <div className='row m-bottom-0'>
          <p className='input-note'>{notes}</p>
        </div>
      )}
    </div>
  )

  function filterOptionStartsWith (option, raw) {
    const search = raw.trim().toLowerCase()
    return option.label
      .toLowerCase()
      .split(/\s+/)
      .some(word => word.startsWith(search))
  }
}

Chosen.defaultProps = {
  isLoading: false,
  isMulti: false,
  isDisabled: false,
  isClearable: false,
  limitSearch: false,
  limitSearchText: '',
  isInvalid: false,
  labelRequired: false,
  isSearchable: true,
  filterOptionMethod: 'startsWith',
  invalidMessage: ''
}

Chosen.propTypes = {
  className: PropTypes.string,
  extraLinkCallback: PropTypes.func,
  extraLinkLabel: PropTypes.string,
  filterOptionMethod: PropTypes.oneOf(['startsWith', 'contains']),
  handleUndefinedValue: PropTypes.bool,
  invalidMessage: PropTypes.string,
  isClearable: PropTypes.bool,
  isDisabled: PropTypes.bool,
  isInvalid: PropTypes.bool,
  isLoading: PropTypes.bool,
  isMulti: PropTypes.bool,
  isSearchable: PropTypes.bool,
  label: PropTypes.string,
  labelRequired: PropTypes.bool,
  limitSearch: PropTypes.bool,
  limitSearchText: PropTypes.string,
  name: PropTypes.string,
  noResultsText: PropTypes.string,
  notes: PropTypes.string,
  onBlur: PropTypes.func,
  onChange: PropTypes.func,
  onInputChange: PropTypes.func,
  onInputReset: PropTypes.func,
  options: PropTypes.array,
  placeholder: PropTypes.string,
  value: PropTypes.any,
  /**
   * Additional values for data-attribute used for tests and tracking
   **/
  'data-mu-test': PropTypes.string
}
